import { useCallback, useEffect, useMemo, useState } from "react";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { storage } from "../helpers/firebaseConfig";

const ENGLISH_DISCLAIMER_TEXT = "Before we start, a quick disclaimer. This evacuation audio guide is based on research from UC Berkeley and is for pre-planning purposes only. In an actual evacuation, always follow official instructions from law enforcement, fire officials, and local emergency authorities.";
const SPANISH_DISCLAIMER_TEXT = "Antes de comenzar, un breve aviso. Esta guía de audio sobre evacuación se basa en investigaciones de UC Berkeley y está destinada únicamente a fines de planificación previa. En una evacuación real, sigue siempre las instrucciones oficiales de las fuerzas del orden, los bomberos y las autoridades locales de emergencias.";

const splitCueIntoParts = ({ text, start, end }, requestedParts) => {
  const words = text.split(/\s+/).filter(Boolean);
  const partCount = Math.min(requestedParts, words.length);
  if (partCount < 2) return [{ text, start, end }];

  return Array.from({ length: partCount }, (_, index) => {
    const wordStart = Math.floor((index * words.length) / partCount);
    const wordEnd = Math.floor(((index + 1) * words.length) / partCount);
    const cueStart = start + ((index * (end - start)) / partCount);
    const cueEnd = start + (((index + 1) * (end - start)) / partCount);
    return {
      text: words.slice(wordStart, wordEnd).join(" "),
      start: cueStart,
      end: cueEnd
    };
  });
};

const splitCue = (cue) => splitCueIntoParts(cue, 2);
const DISCLAIMER_CUES_BY_LANGUAGE = {
  EN: splitCueIntoParts({ text: ENGLISH_DISCLAIMER_TEXT, start: 0, end: 14.5 }, 4),
  ES: splitCueIntoParts({ text: SPANISH_DISCLAIMER_TEXT, start: 0, end: 16.666 }, 4)
};
const findCueText = (cues, time) => cues.find((cue) => time >= cue.start && time < cue.end)?.text || "";

export default function usePodcastCaptions({
  podcastId,
  languageId,
  nodeId,
  mainMediaRef,
  disclaimerAudioRef,
  isDisclaimerPlaying
}) {
  const [cues, setCues] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [mainText, setMainText] = useState("");
  const [disclaimerText, setDisclaimerText] = useState("");
  const disclaimerCues = useMemo(
    () => DISCLAIMER_CUES_BY_LANGUAGE[String(languageId || "EN").toUpperCase()] || [],
    [languageId]
  );

  useEffect(() => {
    const pid = String(podcastId || "").trim();
    const nid = String(nodeId || "").trim();
    const lang = String(languageId || "EN").toUpperCase();
    setCues([]);
    setMainText("");
    if (!pid || !nid) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const url = await getDownloadURL(storageRef(storage, `Podcasts/${pid}/${lang}/${nid}/sentences.json`));
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Caption request failed (${response.status})`);
        const payload = await response.json();
        const nextCues = (Array.isArray(payload) ? payload : [])
          .map((item) => ({
            text: String(item?.sentenceText || "").trim(),
            start: Number.parseFloat(item?.startTime),
            end: Number.parseFloat(item?.endTime)
          }))
          .filter((cue) => cue.text && Number.isFinite(cue.start) && Number.isFinite(cue.end) && cue.end > cue.start)
          .flatMap(splitCue)
          .sort((a, b) => a.start - b.start);
        if (!cancelled) setCues(nextCues);
      } catch (_) {
        if (!cancelled) setCues([]);
      }
    })();

    return () => { cancelled = true; };
  }, [podcastId, languageId, nodeId]);

  const syncMain = useCallback((time) => {
    if (enabled) setMainText(findCueText(cues, time));
  }, [cues, enabled]);

  const syncDisclaimer = useCallback((time) => {
    if (enabled) setDisclaimerText(findCueText(disclaimerCues, time));
  }, [disclaimerCues, enabled]);

  const clearDisclaimer = useCallback(() => setDisclaimerText(""), []);

  const toggle = useCallback(() => {
    setEnabled((wasEnabled) => {
      const nextEnabled = !wasEnabled;
      if (!nextEnabled) {
        setMainText("");
        setDisclaimerText("");
      } else if (isDisclaimerPlaying) {
        setDisclaimerText(findCueText(disclaimerCues, disclaimerAudioRef.current?.currentTime || 0));
      } else {
        setMainText(findCueText(cues, mainMediaRef.current?.currentTime || 0));
      }
      return nextEnabled;
    });
  }, [cues, disclaimerAudioRef, disclaimerCues, isDisclaimerPlaying, mainMediaRef]);

  return useMemo(() => ({
    enabled,
    available: cues.length > 0 || (isDisclaimerPlaying && disclaimerCues.length > 0),
    text: isDisclaimerPlaying ? disclaimerText : mainText,
    toggle,
    syncMain,
    syncDisclaimer,
    clearDisclaimer
  }), [clearDisclaimer, cues.length, disclaimerCues.length, disclaimerText, enabled, isDisclaimerPlaying, mainText, syncDisclaimer, syncMain, toggle]);
}
